/**
 * FreebiePageClient.tsx
 *
 * CYCLE B-FIX (2026-05-22): Consolidated single-subscription Client Component
 * for /free-pdf-downloads/[slug].
 *
 * ARCHITECTURAL FIX — replaces four sibling Client Components that each called
 * useTina() independently at the top level of page.tsx:
 *   - FreebieHeroTextClient    (own useTina — freebie.title, freebie.subhead)
 *   - FreebieSpecificityClient (own useTina — freebie.specificityLine)
 *   - FreebieBulletsClient     (own useTina — freebie.bullets[idx])
 *   - FreebieWhoLarsIsClient   (own useTina — freebie.whoLarsIs[idx])
 *
 * Root cause of the whiteout bug: multiple sibling useTina() subscriptions at
 * the page root create ambiguous resolution in the Tina admin overlay system —
 * the sidebar form unmounts trying to switch contexts, producing visual whiteout.
 *
 * Fix: ONE useTina call for the freebie query. All tinaField markers resolve
 * against the same subscription — unambiguous for the Tina overlay system.
 *
 * LCP PRESERVATION (CYCLE B-FIX-2, 2026-05-22):
 *   Previously: page.tsx built ReactNode props (heroImageNode, avatarImageNode, formNode)
 *   and passed them to this component. ReactNode props crossing the RSC boundary triggered
 *   React "key prop" hydration errors, disrupting Tina form registration → admin whiteout.
 *   Fix: page.tsx passes serializable heroImage data ({ src } | null). This component
 *   imports Image from next/image and FreebieForm, and renders both inline.
 *   LCP preserved: Client Components are SSR'd — the <Image priority> is in initial HTML.
 *
 * tinaField paths rendered here:
 *   freebie.title, freebie.subhead, freebie.specificityLine,
 *   freebie.bullets[0..N], freebie.whoLarsIs[0..1]
 */
'use client';

import Image from 'next/image';
import FreebieForm from '@/components/freebie/FreebieForm';

interface FreebieFormProps {
  ghlTag: string;
  confirmationV1Body: string;
  confirmationV2Body: string;
  resendSubject?: string;
  freebieSlug: string;
}

interface Props {
  query: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  variables: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  /**
   * Whether the freebie has a featuredImage.
   * Mirrors the server-side condition: freebie.featuredImage ? banner : cream.
   */
  hasFeaturedImage: boolean;
  /**
   * Serializable hero image data. Previously passed as heroImageNode (React.ReactNode).
   * ReactNode props crossing the RSC boundary triggered hydration errors (CYCLE B-FIX-2).
   * This component renders <Image fill priority> from this data.
   */
  heroImage: { src: string; phoneSrc?: string } | null;
  /**
   * Serializable FreebieForm props. Previously formNode (React.ReactNode).
   * This component imports FreebieForm and renders it directly.
   */
  freebieFormProps: FreebieFormProps;
  /**
   * Whether to show the Senja testimonial section. Determined server-side.
   */
  senjaTestimonialId?: string | null;
}

export default function FreebiePageClient({
  data,
  hasFeaturedImage,
  heroImage,
  freebieFormProps,
  senjaTestimonialId,
}: Props) {
  const freebie = data.freebie;

  // ── §1 HERO TEXT — H1 + subhead overlay ──────────────────────────────────
  const heroText = (
    <div className="freebie-hero-text">
      <h1
        className="freebie-banner-h1"
      >
        {freebie.title}
      </h1>
      <p
        className="freebie-banner-subhead"
      >
        {freebie.subhead}
      </p>
    </div>
  );

  return (
    <>
      {/*
        §1 HERO — banner layout (with image) or cream fallback (no image).
        heroImage is serializable data; <Image fill priority> rendered inline.
        CYCLE B-FIX-2 (2026-05-22): replaced heroImageNode ReactNode prop.
      */}
      {hasFeaturedImage ? (
        <div
          className={heroImage?.phoneSrc ? 'freebie-hero-banner freebie-hero-banner--phone-crop' : 'freebie-hero-banner'}
          aria-hidden="false"
        >
          <div className="freebie-hero-image-wrap">
            {/* LCP image — rendered inline from serializable heroImage data.
                CYCLE B-FIX-2: was heroImageNode (ReactNode prop) which triggered
                key-prop hydration errors. Client Components are SSR'd so LCP is preserved. */}
            {heroImage && !heroImage.phoneSrc && (
              <Image
                src={heroImage.src}
                alt=""
                fill
                sizes="100vw"
                style={{ objectFit: 'cover', objectPosition: 'center' }}
                priority
                quality={85}
              />
            )}
            {/* Optional per-freebie phone crop (heroImagePhone, 2026-09-28):
                below 768px the browser picks the phone file; the <img> is the
                desktop banner. Only freebies with heroImagePhone render this. */}
            {heroImage && heroImage.phoneSrc && (
              <picture>
                <source media="(max-width: 767px)" srcSet={heroImage.phoneSrc} />
                <Image
                  src={heroImage.src}
                  alt=""
                  fill
                  sizes="100vw"
                  style={{ objectFit: 'cover', objectPosition: 'center' }}
                  priority
                  quality={85}
                />
              </picture>
            )}
            {/* Dark overlay — ensures WCAG AA contrast for H1/subhead text */}
            <div className="freebie-hero-overlay" aria-hidden="true" />
          </div>
          {heroText}
        </div>
      ) : (
        <div
          style={{
            padding: 'var(--space-48) var(--container-pad) 0',
            maxWidth: 'var(--container-max)',
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          {heroText}
        </div>
      )}

      {/*
        §2 DARK FORM BAND — eyebrow, H2, body, form, specificityLine, scroll cue.
        FreebieForm rendered directly via freebieFormProps (CYCLE B-FIX-2: was formNode).
        The specificity line renders here so its tinaField resolves against this subscription.
      */}
      <main id="main-content">
        <section
          className="freebie-form-band"
          aria-labelledby="freebie-form-h2"
        >
          <div className="freebie-form-band-inner">
            {/* CTA block above the form */}
            <div className="freebie-eyebrow" aria-hidden="true">
              <span aria-hidden="true">&#9835;</span>
              <span>FREE PDF</span>
            </div>
            <h2 id="freebie-form-h2">Your Free PDF, in Your Inbox</h2>
            <p className="freebie-cta-body">
              Drop your email and the PDF lands in your inbox in under a minute &mdash; free, no catch, unsubscribe whenever you like.
            </p>
            {/* FreebieForm — rendered directly (CYCLE B-FIX-2: was formNode ReactNode prop) */}
            <FreebieForm
              ghlTag={freebieFormProps.ghlTag}
              confirmationV1Body={freebieFormProps.confirmationV1Body}
              confirmationV2Body={freebieFormProps.confirmationV2Body}
              resendSubject={freebieFormProps.resendSubject}
              freebieSlug={freebieFormProps.freebieSlug}
            />
            {/* Specificity trust line — tinaField resolves against this subscription */}
            <p
              className="freebie-specificity-line freebie-specificity-line--dark"
            >
              {freebie.specificityLine}
            </p>
            {/* Scroll cue */}
            <div className="scroll-cue scroll-cue--freebie" aria-hidden="true">
              Read what&apos;s inside
              <svg className="scroll-cue-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>
        </section>

        {/* §3–§5 BELOW-FOLD SECTIONS — cream background */}
        <div
          className="freebie-below-fold"
          style={{
            background: 'var(--cream)',
            maxWidth: 'var(--container-max)',
            margin: '0 auto',
            textAlign: 'left',
          }}
        >

          {/* §3 What's in the PDF — bullet list */}
          <section
            aria-labelledby="freebie-pdf-contents"
            style={{ padding: 'var(--section-pad-sm) var(--container-pad)' }}
          >
            <div style={{ maxWidth: 640 }}>
              <p
                className="eyebrow--on-cream"
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: 12,
                  letterSpacing: 'var(--eyebrow-tracking)',
                  color: 'var(--eyebrow-on-cream)',
                  marginBottom: 'var(--space-8)',
                }}
              >
                What You&apos;ll Practise
              </p>
              <h2
                id="freebie-pdf-contents"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontWeight: 500,
                  fontSize: 'var(--text-h3)',
                  color: 'var(--text-dark)',
                  marginTop: 'var(--space-8)',
                  marginBottom: 'var(--space-24)',
                }}
              >
                Inside the PDF
              </h2>
              {/* Bullet list with per-item tinaField markers */}
              <ul
                className="freebie-bullets"
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--space-12)',
                }}
              >
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {freebie.bullets.map((bullet: any, idx: number) => (
                  <li
                    key={idx}
                    style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--text-body-mobile)',
                      color: 'var(--text-dark)',
                      lineHeight: 'var(--lh-body)',
                      display: 'flex',
                      gap: 'var(--space-12)',
                      alignItems: 'flex-start',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        color: 'var(--eyebrow-on-cream)',
                        fontFamily: 'var(--font-serif)',
                        fontSize: 20,
                        fontWeight: 600,
                        lineHeight: 1.4,
                        flexShrink: 0,
                        marginTop: 1,
                      }}
                    >
                      &#8250;
                    </span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/*
            §4 Who Lars Is
            Cycle 16: circular avatar LEFT, italic paragraphs RIGHT.
            Cycle 17: align-items: flex-start, marginTop: 4 optical nudge.
            CYCLE B-FIX-2: avatar rendered inline (was avatarImageNode ReactNode prop).
          */}
          <section
            aria-label="About Lars Nelissen"
            style={{ padding: '0 var(--container-pad) var(--section-pad-sm)' }}
          >
            <div
              className="freebie-who-lars-row"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-20)',
                maxWidth: 640,
              }}
            >
              {/* Avatar — rendered inline (CYCLE B-FIX-2: was avatarImageNode ReactNode prop) */}
              <div
                className="freebie-who-lars-avatar-wrap"
                style={{ flexShrink: 0, marginTop: 4 }}
              >
                <Image
                  src="/images/lars-avatar-paris.webp"
                  alt="Lars Nelissen"
                  width={128}
                  height={128}
                  className="freebie-who-lars-avatar"
                  style={{
                    objectFit: 'cover',
                    objectPosition: '20% 8%',
                    borderRadius: '50%',
                    display: 'block',
                  }}
                  priority={false}
                  quality={85}
                />
              </div>
              {/* Italic text block with per-item tinaField markers */}
              <div style={{ maxWidth: 560 }}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {freebie.whoLarsIs.map((line: any, idx: number) => (
                  <p
                    key={idx}
                    style={{
                      fontFamily: 'var(--font-serif)',
                      fontStyle: 'italic',
                      fontWeight: 400,
                      fontSize: 20,
                      color: 'var(--text-dark)',
                      opacity: 0.88,
                      margin: idx === 0 ? 0 : 'var(--space-8) 0 0',
                      lineHeight: 'var(--lh-body)',
                    }}
                  >
                    {line}
                  </p>
                ))}
              </div>
            </div>
          </section>

          {/*
            §5 Testimonial (conditional)
            Senja MCP was unavailable in prior sessions — section omitted per standing rule.
          */}
          {senjaTestimonialId ? (
            <section
              aria-label="What pianists say"
              style={{ padding: '0 var(--container-pad) var(--section-pad-sm)' }}
            >
              <div
                style={{
                  background: 'var(--bg-card-on-cream)',
                  border: '1px solid var(--border-card-on-cream)',
                  borderRadius: 14,
                  padding: '36px 32px',
                  maxWidth: 560,
                }}
              >
                <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-dark-muted)', fontSize: 14 }}>
                  [Senja testimonial {senjaTestimonialId} — Vela to embed once confirmed]
                </p>
              </div>
            </section>
          ) : null}

        </div>
      </main>
    </>
  );
}
